import React, { useRef, HTMLAttributes } from 'react';
import { RadioGroupElementsDirection } from './radio-group';

export type RadioButtonProps = {
    /** Sets "name" attribute of the RadioButton children */
    name?: string,
    /** Sets the direction ("column" | "row") of display for RadioButtons */
    elementsDirection?: RadioGroupElementsDirection,
    /** Sets the selected RadioButton to be the one with its value to be the value of "selected" */
    selected?: boolean,
    /** Sets if the RadioButton should be in card view */
    card?: boolean,
    /** Sets if the RadioButton should be disabled */
    disabled?: boolean;
    /** The children of RadioButton which is used in parallel with the "customLabel" prop */
    children?: React.ReactNode,
    /** Sets if the RadioButton should be in an error state (will override RadioGroup's error prop) */
    error?: boolean,
    /** Sets the value of the RadioButton */
    value: string,
    /** Passes event to radio group in turn value passed to consuming app. */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
    /** Passes event to radio group in turn value passed to consuming app through onChange props. */
    onKeyPress?: (value: string) => void,
}

export const RadioButton = ({
    children,
    error = false,
    name,
    elementsDirection = "row",
    selected,
    card,
    disabled,
    value,
    onChange,
    onKeyPress,
    ...props
  }: RadioButtonProps & HTMLAttributes<HTMLInputElement>) => {

    // HOOKS ##############################################################

    /* useRef */
    const inputRef = useRef();
    const labelRef = useRef();  

    // METHODS #############################################################

    const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // istanbul ignore next
        if (onKeyPress) {
            onKeyPress(e.currentTarget.value);
        }
    };

    const renderChildren = () => {
        // istanbul ignore next
        if (children) {
            return <div>{children}</div>;
        }
    };

    const classes = [
        "radio flex-items-top",
        (elementsDirection === "column" || elementsDirection === "2-column" ) ? "flex" : "inline-flex",
        card ? "card-outline pad-xs" : "mar-t-xs mar-r-sm-all",
        !card ? props.className : "",
        disabled ? "disabled" : "",
        error ? "error" : "",
    ];

    const radioItem = () => {
        return (
            <label
                ref={labelRef}
                htmlFor={props.id}
                className={classes.join(" ").trim()}
            >
                <input
                    id={props.id}
                    aria-describedby={error ? props.id.substring(0, props.id.lastIndexOf("-")) + "errMsg" : undefined}
                    type="radio"
                    name={name}
                    checked={selected}
                    onChange={onChange}
                    onKeyUp={onKeyUp}
                    event-type="keyboard"
                    ref={inputRef}
                    value={value}
                    aria-invalid={error}
                    disabled={disabled}
                />
                <div className={"radio-skin"} />
                <span className="rad-chk-txt">
                    {children ? renderChildren() : value}
                </span>
                <div className={"outline-container"} />
            </label>
        );
    };

  // RENDER ##############################################################
  
  return (
    <React.Fragment>
        {card ? (
            <div
                className={
                    "radio-button-card radius-md rel z0 mar-t-xs " +
                    (!disabled && !error
                        ? selected 
                            ? "selected "
                            : ""
                        : "") +
                    (disabled ? "disabled " : "") +
                    (error ? "error " : "") + (props.className ? props.className : "")
                }
            >
                {radioItem()}
            </div>
        ) : (
            radioItem()
        )}
    </React.Fragment>
  );

};